'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  MessageSquare,
  CheckCircle,
  ArrowUpRight,
  X,
  User,
  Calendar,
  Shield,
  FileText,
  ThumbsUp,
  ThumbsDown,
  Loader2,
} from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

type DisputeStatus = 'open' | 'mediation' | 'resolved' | 'escalated';
type Priority = 'high' | 'medium' | 'low';

interface Dispute {
  id: string;
  job_id: string | null;
  job_title: string;
  customer_name: string;
  artisan_name: string;
  category: string;
  city: string;
  opened_at: string;
  priority: Priority;
  status: DisputeStatus;
  assigned_to: string;
  claim_amount: number;
  description: string;
  customer_claim: string;
  artisan_response: string;
  evidence: string[];
  mediator_notes: string;
  resolution: string | null;
}

const priorityConfig: Record<Priority, { label: string; color: string; bg: string }> = {
  high: { label: 'High', color: 'text-red-700', bg: 'bg-red-50' },
  medium: { label: 'Medium', color: 'text-amber-700', bg: 'bg-amber-50' },
  low: { label: 'Low', color: 'text-blue-700', bg: 'bg-blue-50' },
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB');
}

export default function DisputeManagementContent() {
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'open' | 'mediation' | 'escalated' | 'resolved'>('open');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mediatorNotes, setMediatorNotes] = useState('');
  const [resolveModal, setResolveModal] = useState<{
    open: boolean;
    favor: 'customer' | 'artisan' | 'escalate' | null;
  }>({ open: false, favor: null });
  const [resolving, setResolving] = useState(false);
  const perPage = 6;

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('disputes')
      .select('*')
      .order('opened_at', { ascending: false });
    setDisputes((data as Dispute[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const tabStatuses: Record<string, DisputeStatus[]> = {
    open: ['open'],
    mediation: ['mediation'],
    escalated: ['escalated'],
    resolved: ['resolved'],
  };

  const filtered = useMemo(() => {
    const statuses = tabStatuses[activeTab] ?? ['open'];
    return disputes.filter((d) => {
      const matchTab = statuses.includes(d.status);
      const matchSearch =
        !search ||
        d.id?.toLowerCase().includes(search.toLowerCase()) ||
        d.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        d.artisan_name?.toLowerCase().includes(search.toLowerCase()) ||
        d.job_id?.toLowerCase().includes(search.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [disputes, activeTab, search]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const tabCounts = useMemo(
    () => ({
      open: disputes.filter((d) => d.status === 'open').length,
      mediation: disputes.filter((d) => d.status === 'mediation').length,
      escalated: disputes.filter((d) => d.status === 'escalated').length,
      resolved: disputes.filter((d) => d.status === 'resolved').length,
    }),
    [disputes]
  );

  const openPanel = (dispute: Dispute) => {
    setSelectedDispute(dispute);
    setMediatorNotes(dispute.mediator_notes ?? '');
    setPanelOpen(true);
  };

  const saveMediatorNotes = async () => {
    if (!selectedDispute) return;
    const supabase = createClient();
    const { error } = await supabase
      .from('disputes')
      .update({ mediator_notes: mediatorNotes })
      .eq('id', selectedDispute.id);
    if (error) {
      toast.error('Failed to save notes.');
    } else {
      toast.success('Mediator notes saved');
      await fetchDisputes();
    }
  };

  const handleResolve = async () => {
    if (!resolveModal.favor || !selectedDispute) return;
    setResolving(true);
    const supabase = createClient();

    const statusMap: Record<string, string> = {
      customer: 'resolved',
      artisan: 'resolved',
      escalate: 'escalated',
    };

    const resolutionMap: Record<string, string> = {
      customer: `Resolved in favor of Customer — refund of OMR ${selectedDispute.claim_amount} initiated`,
      artisan: `Resolved in favor of Artisan — payment of OMR ${selectedDispute.claim_amount} released`,
      escalate: `Escalated to senior administrator`,
    };

    const { error } = await supabase
      .from('disputes')
      .update({
        status: statusMap[resolveModal.favor],
        resolution: resolutionMap[resolveModal.favor],
        resolved_at: new Date().toISOString(),
      })
      .eq('id', selectedDispute.id);

    if (error) {
      toast.error('Failed to resolve dispute. Please try again.');
    } else {
      const messages: Record<string, string> = {
        customer: `Dispute ${selectedDispute.id} resolved in favor of customer — refund initiated`,
        artisan: `Dispute ${selectedDispute.id} resolved in favor of artisan — payment released`,
        escalate: `Dispute ${selectedDispute.id} escalated to senior admin`,
      };
      toast.success(messages[resolveModal.favor]);
      await fetchDisputes();
    }

    setResolving(false);
    setResolveModal({ open: false, favor: null });
    setPanelOpen(false);
  };

  if (loading) {
    return (
      <div className="px-6 py-6 max-w-screen-2xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`skel-${i}`} className="card-elevated rounded-xl px-4 py-3 h-16 animate-pulse bg-muted" />
          ))}
        </div>
        <div className="card-elevated rounded-xl overflow-hidden">
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={`row-skel-${i}`} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { key: 'open', label: 'Open Disputes', color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
          { key: 'mediation', label: 'In Mediation', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
          { key: 'escalated', label: 'Escalated', color: 'text-red-800', bg: 'bg-red-100 border-red-300' },
          { key: 'resolved', label: 'Resolved', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
        ].map((s) => (
          <div key={`dstat-${s.key}`} className={`card-elevated rounded-xl px-4 py-3 border ${s.bg}`}>
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className={`text-2xl font-700 font-tabular ${s.color}`}>
              {tabCounts[s.key as keyof typeof tabCounts]}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-6 mb-4 border-b border-border">
        {(
          [
            { key: 'open', label: 'Open' },
            { key: 'mediation', label: 'In Mediation' },
            { key: 'escalated', label: 'Escalated' },
            { key: 'resolved', label: 'Resolved' },
          ] as const
        ).map((tab) => (
          <button
            key={`dtab-${tab.key}`}
            onClick={() => { setActiveTab(tab.key); setPage(1); }}
            className={`flex items-center gap-2 pb-3 text-sm font-600 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            <span
              className={`px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === tab.key ? 'bg-accent/10 text-accent' : 'bg-muted text-muted-foreground'
              }`}
            >
              {tabCounts[tab.key]}
            </span>
          </button>
        ))}

        <div className="ml-auto flex items-center gap-2 bg-muted rounded-lg px-3 py-1.5 mb-3">
          <Search size={13} className="text-muted-foreground" />
          <input
            type="text"
            placeholder="Search disputes..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-44"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card-elevated rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Dispute ID</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Job Ref</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Customer</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Artisan</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Category</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Opened</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Claim</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Assigned To</th>
                <th className="w-20 px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-16 text-center">
                    <CheckCircle size={32} className="text-accent mx-auto mb-3" />
                    <p className="text-sm font-600 text-foreground mb-1">No {activeTab} disputes</p>
                    <p className="text-xs text-muted-foreground">
                      {activeTab === 'resolved' ?'No disputes have been resolved yet.'
                        : `There are no ${activeTab} disputes at this time.`}
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.map((dispute) => {
                  const pConfig = priorityConfig[dispute.priority];
                  return (
                    <tr key={dispute.id} className="table-row-hover transition-colors group">
                      <td className="px-4 py-3.5">
                        <span className="text-xs font-700 font-mono text-primary">{dispute.id}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-xs font-600 text-foreground">{dispute.job_id ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <User size={12} className="text-muted-foreground shrink-0" />
                          <span className="text-xs text-foreground">{dispute.customer_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <User size={12} className="text-muted-foreground shrink-0" />
                          <span className="text-xs text-foreground">{dispute.artisan_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-xs font-500 bg-primary/8 text-primary px-2 py-0.5 rounded-full whitespace-nowrap">
                          {dispute.category}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                          <Calendar size={11} />
                          {formatDate(dispute.opened_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${pConfig.bg} ${pConfig.color}`}>
                          {pConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-700 font-tabular text-foreground">
                          OMR {(dispute.claim_amount ?? 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-xs ${dispute.assigned_to === 'Unassigned' ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                          {dispute.assigned_to}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => openPanel(dispute)}
                            title="Open mediation panel"
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            title="View messages"
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                          >
                            <MessageSquare size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {Math.min((page - 1) * perPage + 1, filtered.length)}–{Math.min(page * perPage, filtered.length)} of{' '}
            <span className="font-600 text-foreground">{filtered.length}</span> disputes
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`dpage-${i + 1}`}
                onClick={() => setPage(i + 1)}
                className={`w-8 h-8 text-xs rounded-lg font-600 transition-colors ${
                  page === i + 1
                    ? 'bg-primary text-white' :'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Mediation panel drawer */}
      {panelOpen && selectedDispute && (
        <div
          className="fixed inset-0 z-40 bg-black/30 fade-in"
          onClick={() => setPanelOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-full max-w-2xl bg-card shadow-modal overflow-y-auto scrollbar-thin"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel header */}
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-700 text-foreground">{selectedDispute.id}</h2>
                  <StatusBadge status={selectedDispute.status} />
                  <span
                    className={`text-xs font-600 px-2 py-0.5 rounded-full ${priorityConfig[selectedDispute.priority].bg} ${priorityConfig[selectedDispute.priority].color}`}
                  >
                    {priorityConfig[selectedDispute.priority].label} Priority
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {selectedDispute.job_id} · {selectedDispute.job_title}
                </p>
              </div>
              <button
                onClick={() => setPanelOpen(false)}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Parties */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <p className="text-xs font-600 text-blue-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <User size={11} />
                    Customer (Claimant)
                  </p>
                  <p className="text-sm font-700 text-foreground">{selectedDispute.customer_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedDispute.city} · {selectedDispute.category}
                  </p>
                  <p className="text-xs font-600 text-blue-700 mt-2">
                    Claiming: OMR {(selectedDispute.claim_amount ?? 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <p className="text-xs font-600 text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Shield size={11} />
                    Artisan (Respondent)
                  </p>
                  <p className="text-sm font-700 text-foreground">{selectedDispute.artisan_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {selectedDispute.city} · {selectedDispute.category}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Assigned to: {selectedDispute.assigned_to}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Dispute Summary</h4>
                <p className="text-sm text-foreground leading-relaxed bg-muted/40 rounded-lg p-4">
                  {selectedDispute.description}
                </p>
              </div>

              {/* Claims */}
              <div className="space-y-3">
                <h4 className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Statements</h4>
                <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/50">
                  <p className="text-xs font-600 text-blue-700 mb-2 flex items-center gap-1">
                    <User size={11} />
                    Customer Statement
                  </p>
                  <p className="text-sm text-foreground leading-relaxed">&ldquo;{selectedDispute.customer_claim}&rdquo;</p>
                </div>
                {selectedDispute.artisan_response ? (
                  <div className="border border-amber-200 rounded-xl p-4 bg-amber-50/50">
                    <p className="text-xs font-600 text-amber-700 mb-2 flex items-center gap-1">
                      <Shield size={11} />
                      Artisan Response
                    </p>
                    <p className="text-sm text-foreground leading-relaxed">&ldquo;{selectedDispute.artisan_response}&rdquo;</p>
                  </div>
                ) : (
                  <div className="border border-border rounded-xl p-4 bg-muted/30">
                    <p className="text-xs text-muted-foreground italic">Artisan has not yet responded to this dispute.</p>
                  </div>
                )}
              </div>

              {/* Evidence */}
              {(selectedDispute.evidence?.length ?? 0) > 0 && (
                <div>
                  <h4 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-3">
                    Evidence ({selectedDispute.evidence?.length} items)
                  </h4>
                  <div className="space-y-2">
                    {selectedDispute.evidence?.map((item, idx) => (
                      <div
                        key={`evidence-${selectedDispute.id}-${idx + 1}`}
                        className="flex items-center gap-3 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors cursor-pointer"
                      >
                        <FileText size={14} className="text-muted-foreground shrink-0" />
                        <span className="text-sm text-foreground flex-1">{item}</span>
                        <Eye size={13} className="text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resolution info */}
              {selectedDispute.resolution && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-xs font-600 text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <CheckCircle size={11} />
                    Resolution
                  </p>
                  <p className="text-sm text-foreground">{selectedDispute.resolution}</p>
                </div>
              )}

              {/* Mediator notes */}
              <div>
                <h4 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">
                  Mediator Notes
                </h4>
                <textarea
                  rows={4}
                  value={mediatorNotes}
                  onChange={(e) => setMediatorNotes(e.target.value)}
                  placeholder="Add your mediation notes, findings, and recommendations here..."
                  className="w-full px-3 py-2.5 border border-border rounded-lg text-sm text-foreground bg-card placeholder:text-muted-foreground outline-none resize-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  disabled={selectedDispute.status === 'resolved'}
                />
                {selectedDispute.status !== 'resolved' && (
                  <button
                    onClick={saveMediatorNotes}
                    className="mt-2 text-xs font-600 text-accent hover:underline"
                  >
                    Save notes
                  </button>
                )}
              </div>

              {/* Trust score impact */}
              {selectedDispute.status !== 'resolved' && (
                <div className="bg-muted/50 border border-border rounded-xl p-4">
                  <h4 className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-3">
                    Trust Score Impact
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-sm">
                      <p className="font-600 text-foreground mb-1">If resolved for Customer</p>
                      <p className="text-xs text-red-600">Artisan trust score: −0.4 pts</p>
                      <p className="text-xs text-muted-foreground">
                        Full refund of OMR {(selectedDispute.claim_amount ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-sm">
                      <p className="font-600 text-foreground mb-1">If resolved for Artisan</p>
                      <p className="text-xs text-muted-foreground">No trust score change</p>
                      <p className="text-xs text-muted-foreground">Payment released to artisan</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              {selectedDispute.status !== 'resolved' && (
                <div className="flex flex-wrap items-center gap-3 pt-2 pb-4">
                  <button
                    onClick={() => setResolveModal({ open: true, favor: 'customer' })}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ThumbsUp size={14} />
                    Resolve for Customer
                  </button>
                  <button
                    onClick={() => setResolveModal({ open: true, favor: 'artisan' })}
                    className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white text-sm font-600 rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    <ThumbsDown size={14} />
                    Resolve for Artisan
                  </button>
                  <button
                    onClick={() => setResolveModal({ open: true, favor: 'escalate' })}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-600 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <ArrowUpRight size={14} />
                    Escalate
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resolution confirm modal */}
      <Modal
        open={resolveModal.open}
        onClose={() => setResolveModal({ open: false, favor: null })}
        title={
          resolveModal.favor === 'customer' ?'Resolve in Favor of Customer'
            : resolveModal.favor === 'artisan' ?'Resolve in Favor of Artisan' :'Escalate Dispute'
        }
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          {resolveModal.favor === 'customer'
            ? `This will issue a full refund of OMR ${selectedDispute?.claim_amount?.toLocaleString()} to the customer and reduce the artisan trust score. This action cannot be undone.`
            : resolveModal.favor === 'artisan'
            ? `This will release the full payment to the artisan and close the dispute. This action cannot be undone.`
            : `This will escalate the dispute to a senior administrator and pause any automatic resolution.`}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setResolveModal({ open: false, favor: null })}
            className="btn-ghost border border-border"
          >
            Cancel
          </button>
          <button
            onClick={handleResolve}
            disabled={resolving}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-600 rounded-lg text-white transition-colors disabled:opacity-70 disabled:cursor-not-allowed ${
              resolveModal.favor === 'customer' ?'bg-blue-600 hover:bg-blue-700'
                : resolveModal.favor === 'artisan' ?'bg-amber-600 hover:bg-amber-700' :'bg-red-600 hover:bg-red-700'
            }`}
          >
            {resolving && <Loader2 size={14} className="animate-spin" />}
            {resolveModal.favor === 'customer' ?'Confirm Refund'
              : resolveModal.favor === 'artisan' ?'Release Payment' :'Escalate Now'}
          </button>
        </div>
      </Modal>
    </div>
  );
}